import { gql, useMutation } from '@apollo/client'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { type FormEvent, useMemo, useState } from 'react'
import { useLocale, useNotify, useTranslate } from 'react-admin'

/**
 * The Boatvertizer login the server fetches the figures with. Written through
 * an action to the feed service, which stores the password encrypted; this
 * page never reads it back — it sees the username and how the last fetch went.
 * The password travels as a GraphQL variable: Hasura logs query text outside
 * development, never variables.
 */
const SAVE_LOGIN = gql`
  mutation SaveBoatvertizerLogin($username: String!, $password: String!) {
    boatvertizer_save_login(username: $username, password: $password) {
      saved
      username
    }
  }
`

const FETCH = gql`
  mutation FetchBoatvertizer {
    boatvertizer_fetch {
      ok
      message
      listings
    }
  }
`

export interface Account {
  username: string
  saved_at: string
  last_fetch_at: string | null
  last_fetch_ok: boolean | null
  last_fetch_message: string | null
}

export const BoatvertizerAccount = ({
  account,
  onChanged,
}: {
  account: Account | null
  onChanged: () => void
}) => {
  const translate = useTranslate()
  const notify = useNotify()
  const locale = useLocale()
  const [editing, setEditing] = useState(!account)
  const [username, setUsername] = useState(account?.username ?? '')
  const [password, setPassword] = useState('')
  const [saveLogin, { loading: saving }] = useMutation(SAVE_LOGIN)
  const [fetchNow, { loading: fetching }] = useMutation(FETCH)
  const [error, setError] = useState<string | null>(null)

  const date = useMemo(() => new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }), [locale])
  const relative = useMemo(() => {
    const format = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
    return (iso: string) => {
      const minutes = Math.round((Date.parse(iso) - Date.now()) / 60_000)
      if (Math.abs(minutes) < 60) return format.format(minutes, 'minute')
      const hours = Math.round(minutes / 60)
      return Math.abs(hours) < 48
        ? format.format(hours, 'hour')
        : format.format(Math.round(hours / 24), 'day')
    }
  }, [locale])

  const runFetch = async () => {
    setError(null)
    try {
      await fetchNow()
    } catch (e: any) {
      setError(e.message)
    }
    onChanged()
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      await saveLogin({ variables: { username: username.trim(), password } })
    } catch (err: any) {
      setError(err.message)
      return
    }
    setPassword('')
    setEditing(false)
    notify('feed.bv.account.saved', { type: 'info' })
    onChanged()
    await runFetch()
  }

  return (
    <Box sx={{ mb: 3 }} data-testid='bv-account'>
      <Typography variant='subtitle1'>{translate('feed.bv.account.title')}</Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5, maxWidth: 820 }}>
        {translate('feed.bv.account.intro')}
      </Typography>

      {error && (
        <Alert severity='error' sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {editing ? (
        <Box component='form' onSubmit={submit} autoComplete='off'>
          <Grid container spacing={2} sx={{ alignItems: 'center' }}>
            <Grid size={{ xs: 12, sm: 5, md: 4 }}>
              <TextField
                fullWidth
                size='small'
                name='boatvertizer-username'
                label={translate('feed.bv.account.username')}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                slotProps={{ htmlInput: { autoComplete: 'off' } }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <TextField
                fullWidth
                size='small'
                type='password'
                name='boatvertizer-password'
                label={translate('feed.bv.account.password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                // Not the signed-in user's CMS password, which a browser would offer.
                slotProps={{ htmlInput: { autoComplete: 'new-password' } }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3, md: 5 }}>
              <Stack direction='row' spacing={1}>
                <Button
                  type='submit'
                  variant='contained'
                  disabled={saving || fetching || !username.trim() || !password}
                >
                  {translate('feed.bv.account.save')}
                </Button>
                {account && (
                  <Button onClick={() => setEditing(false)}>
                    {translate('feed.bv.account.cancel')}
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Box>
      ) : (
        account && (
          <Box>
            <Typography variant='body2' sx={{ mb: 1 }}>
              {translate('feed.bv.account.stored', {
                username: account.username,
                date: date.format(new Date(account.saved_at)),
              })}{' '}
              {translate('feed.bv.account.daily')}
            </Typography>
            {fetching ? (
              <Alert severity='info' icon={<CircularProgress size={18} />} sx={{ mb: 1 }}>
                {translate('feed.bv.account.fetching')}
              </Alert>
            ) : account.last_fetch_at ? (
              <Alert
                severity={account.last_fetch_ok ? 'success' : 'error'}
                sx={{ mb: 1 }}
                data-testid='bv-last-fetch'
              >
                {translate(
                  account.last_fetch_ok ? 'feed.bv.account.last_ok' : 'feed.bv.account.last_failed',
                  {
                    when: relative(account.last_fetch_at),
                    message: account.last_fetch_message ?? '',
                  }
                )}
              </Alert>
            ) : (
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                {translate('feed.bv.account.never')}
              </Typography>
            )}
            <Stack direction='row' spacing={1}>
              <Button
                variant='outlined'
                startIcon={<RefreshIcon />}
                disabled={fetching}
                onClick={runFetch}
              >
                {translate('feed.bv.account.fetch')}
              </Button>
              <Button
                onClick={() => {
                  setUsername(account.username)
                  setPassword('')
                  setEditing(true)
                }}
              >
                {translate('feed.bv.account.change')}
              </Button>
            </Stack>
          </Box>
        )
      )}
    </Box>
  )
}

export default BoatvertizerAccount
